import SearchToken from "./search_token";
// `SearchParser` is where search string parsing and manipulation
// is located. Functions living here should not have any side-effects.

const SearchParser = {
  // Parses search queries into an array of substrings.
  //
  // Substrings are separated when:
  //   * Whitespace and "regular" characters are adjacent,
  //     unless they're surround by '', "" of []. Regular
  //     characters are everything except whitespace and '', "" and [].
  //   * A "suround" is closed. ie, the final ', " or ]
  //
  // One important result of these rules is that whitespace is
  // always its own token. That is `"foo:1    bar:2"` is parsed to
  // `["foo:1", "    ", "bar:2"]`. This allows us to replace tokens without
  // altering surrounding whitespace. If we did alter whitespace, it would
  // be hard to know where to place the text input's cursor after changing
  // the search string...and cursor position determines which hints are shown!
  //
  parse(text) {
    const WHITESPACE = /\s/;
    const NON_WHITESPACE = /\S/;

    let surround = null;
    const output = [];
    let buffer = "";

    const flush = () => {
      if (buffer !== "") {
        output.push(buffer);
      }
      buffer = "";
    };

    const previous = () => buffer[buffer.length - 1] || "";

    const cleanText = text
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/[\u201C\u201D]/g, '"');

    Array.from(cleanText).forEach((c) => {
      if (c.match(WHITESPACE)) {
        if (previous().match(NON_WHITESPACE) && !surround) {
          flush();
        }
        buffer += c;
      } else if (c === '"' || c === "'") {
        buffer += c;
        if (!surround) {
          surround = c;
        } else if (surround === c) {
          flush();
          surround = null;
        }
      } else if (c === "[") {
        buffer += c;
        if (!surround) {
          surround = c;
        }
      } else if (c === "]") {
        buffer += c;
        if (surround === "[") {
          flush();
          surround = null;
        }
      } else {
        if (previous().match(WHITESPACE) && !surround) {
          flush();
        }
        buffer += c;
      }
    });

    flush();
    return this.combine(output);
  },

  // Plain-text search terms can be sprinkled throughout structured
  // search terms. These don't require quotes for multi-word phrases.
  // So we step through our parse output and join together any unstructured
  // search terms into a single term:
  //
  // For example:
  //
  // "  foo bar baz:1" => ["  ", "foo bar", " ", "baz:1"]
  //
  combine(terms) {
    const result = [];
    let combining = false;

    terms.forEach((t) => {
      // If the current term is like key:value, push it to
      // the results and exit "combining" mode. Also, remove
      // the last previously combined term and push it back in
      // uncombined form.
      if (t.match(/^[^:]+:/)) {
        if (combining) {
          const previous = result[result.length - 1].pop();
          result.push([previous]);
          combining = false;
        }
        result.push([t]);
        return;
      }

      // If the term is whitespace, either append it to the combined item
      // or push it to results by itself.
      if (t.match(/^\s+$/)) {
        if (combining) {
          result[result.length - 1].push(t);
        } else {
          result.push([t]);
        }
        return;
      }

      // Otherwise we either push or append the item based on whether
      // it's the first in a series.
      if (combining) {
        result[result.length - 1].push(t);
      } else {
        result.push([t]);
        combining = true;
      }
    });

    // flatten the array
    return result.map((r) => r.join(""));
  },

  tokenize(text) {
    return this.wrap(this.parse(text));
  },

  wrap(tokens) {
    let offset = 0;

    return tokens.map((t, i) => {
      offset += t.length;
      return new SearchToken(t, i, offset - t.length, offset);
    });
  },

  tokenAtOffset(text, offset, tokens) {
    return (tokens || this.tokenize(text)).find(
      (t) => t.startOffset < offset && offset <= t.endOffset,
    );
  },

  tokenAtCursor(tokens, cursor) {
    if (cursor === 0) {
      return tokens[0];
    }
    const token = this.tokenAtOffset(null, cursor, tokens);
    if (
      token &&
      token.isWhitespace() &&
      cursor === token.endOffset &&
      token.index < tokens.length - 1
    ) {
      return tokens[token.index + 1];
    }
    return token;
  },

  replaceToken(text, searchToken) {
    const pieces = this.parse(text);
    pieces[searchToken.index] = searchToken.text;
    return this.wrap(pieces);
  },

  tokensNotStartingWith(text, prefix) {
    const tokens = this.tokenize(text);
    if (!prefix) {
      return tokens;
    }
    const results = [];
    let i = 0;
    while (i < tokens.length) {
      if (tokens[i].text.startsWith(prefix)) {
        i += 2;
      } else {
        results.push(tokens[i]);
        i += 1;
      }
    }
    return results;
  },

  toString(tokens) {
    return Array.from(tokens)
      .map((t) => t.text)
      .join("");
  },
};
export default SearchParser;
